import React, { useState } from 'react';
import { generateContent } from '../utils/ai';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Slider,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Collapse
} from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

const QUESTION_TYPES = [
  { value: 'mcq', label: 'MCQ' },
  { value: 'truefalse', label: 'True/False' },
  { value: 'short', label: 'Short Answer' },
];
const DIFFICULTIES = ['Easy', 'Medium', 'Hard'];
const FREE_LIMIT = 5;
const PRO_LIMIT = 10;
const EXAM_LIMIT = 20;

// Placeholder: Replace with real user/pro logic
const isProUser = false; // TODO: connect to auth/user state
const comingFromExamMode = false; // TODO: connect to navigation state
const maxQuestions = comingFromExamMode ? EXAM_LIMIT : (isProUser ? PRO_LIMIT : FREE_LIMIT);

export default function AIPracticeQuestions() {
  // Form state
  const [subject, setSubject] = useState('');
  const [topics, setTopics] = useState('');
  const [questionType, setQuestionType] = useState('mcq');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [difficulty, setDifficulty] = useState('Easy');
  const [numQuestions, setNumQuestions] = useState(maxQuestions);

  // Status/feedback state
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]); // [{question, options, answer, explanation, type}, ...]
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState([]); // user answers
  const [submitted, setSubmitted] = useState(false);
  const [showProDialog, setShowProDialog] = useState(false);

  // Handlers
  const handleGenerate = async () => {
    setError('');
    if (!topics.trim()) {
      setError('Please enter a topic or prompt.');
      return;
    }
    if (!isProUser && numQuestions > FREE_LIMIT) {
      setShowProDialog(true);
      return;
    }
    setLoading(true);
    try {
      // Compose prompt for question generation
      const systemPrompt = `You are an expert exam question generator. Generate a set of high-quality, original practice questions for the given subject and topics. Each question should include:
- The question text
- Options (for MCQ or True/False)
- The correct answer (index or value)
- A brief explanation
- The question type (mcq, truefalse, short)
Output as a JSON array of objects with keys: question, options, answer, explanation, type.`;
      const userPrompt = `Subject: ${subject}\nTopics: ${topics}\nQuestion Type: ${questionType}\nDifficulty: ${difficulty}\nNumber of Questions: ${numQuestions}`;
      const messages = [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ];
      const response = await generateContent(messages, { max_tokens: 2000, temperature: 0.7 });
      // Try to parse the response as JSON
      let parsed;
      try {
        parsed = JSON.parse(response);
      } catch (e) {
        // Attempt to extract JSON from markdown/code block if present
        const match = response.match(/```json[\s\S]*?([\[{].*[\]}])[\s\S]*?```/i) || response.match(/([\[{][\s\S]*[\]}])/);
        if (match) {
          try {
            parsed = JSON.parse(match[1]);
          } catch (err) {
            throw new Error('AI response could not be parsed as questions.');
          }
        } else {
          throw new Error('AI did not return questions in JSON format.');
        }
      }
      // Validate array
      if (!Array.isArray(parsed) || parsed.length === 0) {
        throw new Error('No questions were generated.');
      }
      setQuestions(parsed);
      setAnswers(Array(parsed.length).fill(null));
      setCurrentIdx(0);
      setSubmitted(false);
    } catch (err) {
      setError(err.message || 'Failed to generate questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOptionSelect = (idx) => {
    if (submitted) return;
    setAnswers((prev) => {
      const updated = [...prev];
      updated[currentIdx] = idx;
      return updated;
    });
  };

  const handleShortAnswer = (e) => {
    if (submitted) return;
    setAnswers((prev) => {
      const updated = [...prev];
      updated[currentIdx] = e.target.value;
      return updated;
    });
  };

  const handlePrev = () => setCurrentIdx((i) => Math.max(i - 1, 0));
  const handleNext = () => setCurrentIdx((i) => Math.min(i + 1, questions.length - 1));
  const handleSubmit = () => setSubmitted(true);
  const handleTryAgain = () => setSubmitted(false);
  const handleNewQuestions = () => {
    setQuestions([]);
    setAnswers([]);
    setCurrentIdx(0);
    setSubmitted(false);
  };

  // Score calculation (for MCQ/TrueFalse)
  const score = submitted && questions.length > 0
    ? Math.round(
        (answers.filter((ans, i) => ans === questions[i].answer).length / questions.length) * 100
      )
    : null;

  // UI Rendering
  return (
    <Container maxWidth="md"
      sx={(theme) => ({
        py: 4,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #23272f 0%, #181c22 100%)'
          : 'linear-gradient(135deg, #e3f2fd 0%, #fce4ec 100%)'
      })}
    >
  <Paper elevation={4} sx={(theme) => ({
    borderRadius: 4,
    p: { xs: 2, sm: 5 },
    width: '100%',
    boxShadow: 8,
    background: theme.palette.mode === 'dark' ? '#23272f' : '#fafafa',
    color: theme.palette.mode === 'dark' ? '#fff' : 'inherit'
  })}>
    {/* Header */}
    <Typography variant="h3" fontWeight={800} align="center" gutterBottom color="primary.main" letterSpacing={1}>
      <span role="img" aria-label="AI">🤖</span> AI Practice Questions
    </Typography>
    <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 2 }}>
      Instantly generate, answer, and review smart practice questions for any subject.
    </Typography>
    <Divider sx={{ mb: 3 }} />

    {/* Form Section */}
    {questions.length === 0 && (
      <Card elevation={2} sx={{ maxWidth: 520, mx: 'auto', mb: 3, p: 2, borderRadius: 3, background: '#fafafa' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            1. Enter Details
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Subject (optional)"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              aria-label="Subject"
            />
            <TextField
              label="Prompt or Topic"
              value={topics}
              onChange={(e) => setTopics(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              aria-label="Prompt or Topic"
              placeholder="Type your topic, question, or prompt here..."
            />
            <Typography variant="h6" fontWeight={700} gutterBottom>
              2. Choose Question Type
            </Typography>
            <ToggleButtonGroup
              value={questionType}
              exclusive
              onChange={(_, val) => val && setQuestionType(val)}
              fullWidth
              color="primary"
              sx={{ justifyContent: 'center', mb: 1 }}
              aria-label="Question Type"
            >
              {QUESTION_TYPES.map((qt) => (
                <ToggleButton key={qt.value} value={qt.value} aria-label={qt.label}>{qt.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Button variant="outlined" onClick={() => setShowAdvanced((v) => !v)} sx={{ alignSelf: 'flex-end' }}>
              {showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}
            </Button>
            <Collapse in={showAdvanced}>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  3. Advanced Options
                </Typography>
                <ToggleButtonGroup
                  value={difficulty}
                  exclusive
                  onChange={(_, val) => val && setDifficulty(val)}
                  fullWidth
                  color="secondary"
                  sx={{ justifyContent: 'center' }}
                  aria-label="Difficulty"
                >
                  {DIFFICULTIES.map((d) => (
                    <ToggleButton key={d} value={d} aria-label={d}>{d}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Box sx={{ px: 1 }}>
                  <Typography gutterBottom>Number of Questions: <b>{numQuestions}</b></Typography>
                  <Slider
                    value={numQuestions}
                    min={1}
                    max={maxQuestions}
                    step={1}
                    marks={[{ value: FREE_LIMIT, label: 'Free' }, { value: PRO_LIMIT, label: 'Pro' }, comingFromExamMode && { value: EXAM_LIMIT, label: 'Exam' }].filter(Boolean)}
                    onChange={(_, val) => setNumQuestions(val)}
                    valueLabelDisplay="on"
                    sx={{ color: 'primary.main' }}
                    aria-label="Number of Questions"
                  />
                </Box>
                {!isProUser && numQuestions > FREE_LIMIT && (
                  <Alert severity="info" icon={<InfoOutlined color="primary" />}>Upgrade to Pro to generate more than {FREE_LIMIT} questions at once.</Alert>
                )}
              </Stack>
            </Collapse>
            <Button variant="contained" size="large" onClick={handleGenerate} disabled={loading} sx={{ mt: 2 }} aria-label="Generate Questions">
              {loading ? <CircularProgress size={24} /> : 'Generate Questions'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    )}
    {/* Success Message */}
    {!loading && questions.length > 0 && !error && (
      <Alert severity="success" sx={{ mb: 2, fontWeight: 500 }} icon={<CheckIcon fontSize="inherit" color="success" />}>
        Questions generated! Start practicing below.
      </Alert>
    )}


        {/* Status/Feedback */}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>}

        {/* Questions Section */}
        {!loading && questions.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Card elevation={2} sx={{ mb: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Chip
                    label={`${currentIdx + 1}/${questions.length}`}
                    color="primary"
                  />
                  <Chip
                    label={questions[currentIdx].type.toUpperCase()}
                    color="secondary"
                  />
                </Stack>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  {questions[currentIdx].question}
                </Typography>
                {questions[currentIdx].type === 'mcq' && (
                  <Grid container spacing={2}>
                    {questions[currentIdx].options.map((opt, idx) => (
                      <Grid item xs={6} sm={3} key={idx}>
                        <Button
                          fullWidth
                          variant={answers[currentIdx] === idx ? 'contained' : 'outlined'}
                          color={
                            submitted
                              ? idx === questions[currentIdx].answer
                                ? 'success'
                                : answers[currentIdx] === idx
                                ? 'error'
                                : 'primary'
                              : 'primary'
                          }
                          endIcon={
                            submitted && idx === questions[currentIdx].answer ? <CheckIcon /> :
                            submitted && answers[currentIdx] === idx && idx !== questions[currentIdx].answer ? <CloseIcon /> : null
                          }
                          onClick={() => handleOptionSelect(idx)}
                          disabled={submitted}
                        >
                          {String.fromCharCode(65 + idx)}. {opt}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                )}
                {questions[currentIdx].type === 'truefalse' && (
                  <Stack direction="row" spacing={2} justifyContent="center">
                    {['True', 'False'].map((opt, idx) => (
                      <Button
                        key={opt}
                        variant={answers[currentIdx] === idx ? 'contained' : 'outlined'}
                        color={
                          submitted
                            ? idx === questions[currentIdx].answer
                              ? 'success'
                              : answers[currentIdx] === idx
                              ? 'error'
                              : 'primary'
                            : 'primary'
                        }
                        endIcon={
                          submitted && idx === questions[currentIdx].answer ? <CheckIcon /> :
                          submitted && answers[currentIdx] === idx && idx !== questions[currentIdx].answer ? <CloseIcon /> : null
                        }
                        onClick={() => handleOptionSelect(idx)}
                        disabled={submitted}
                        sx={{ minWidth: 100 }}
                      >
                        {opt}
                      </Button>
                    ))}
                  </Stack>
                )}
                {questions[currentIdx].type === 'short' && (
                  <TextField
                    label="Your Answer"
                    value={answers[currentIdx] || ''}
                    onChange={handleShortAnswer}
                    fullWidth
                    disabled={submitted}
                  />
                )}
                {submitted && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <strong>Explanation:</strong> {questions[currentIdx].explanation}
                  </Alert>
                )}
              </CardContent>
            </Card>
            {/* Navigation */}
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 2 }}>
              <Button onClick={handlePrev} disabled={currentIdx === 0}>Previous</Button>
              {currentIdx < questions.length - 1 ? (
                <Button onClick={handleNext}>Next</Button>
              ) : !submitted ? (
                <Button variant="contained" color="primary" onClick={handleSubmit}>Submit</Button>
              ) : null}
            </Stack>
            {/* After Submission */}
            {submitted && (
              <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
                <Typography variant="h6" color="primary">
                  Score: {score}%
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button onClick={handleTryAgain}>Try Again</Button>
                  <Button variant="outlined" onClick={handleNewQuestions}>New Questions</Button>
                </Stack>
              </Stack>
            )}
          </Box>
        )}
      </Paper>
      {/* Pro Features Dialog */}
      <Dialog open={showProDialog} onClose={() => setShowProDialog(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span role="img" aria-label="Pro">🚀</span> Unlock Pro Features
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            You’ve reached the <b>free limit</b> of {FREE_LIMIT} questions per session.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Upgrade to Pro for:
          </Typography>
          <ul style={{ marginLeft: 20, marginBottom: 12, color: '#333', fontSize: '1rem' }}>
            <li>Up to <b>{PRO_LIMIT}</b> questions per session</li>
            <li>Advanced analytics and explanations</li>
            <li>Priority support</li>
            <li>And more!</li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowProDialog(false)} variant="outlined">Cancel</Button>
          <Button variant="contained" color="secondary" onClick={() => {/* TODO: handle upgrade */}}>Upgrade</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
